import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import SEO from "../../components/SEO";

const WCFY_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/kd42ok0u_FosterCareAnthem_WereComingForYou_2026.png";
const VOL1_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/u1pk93uo_FosterCareAnthem_VOL1_Cover.png";
const EDITIONS_COVER = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/0z2z5cvc_FosterCareAnthem_VOL1_AnthemEditionsCover.png";
const HERO_IMG = "https://fostercareanthemproject.com/gallery/awareness/if-we-dont-1.jpg";

export default function AnthemHome() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="anthem-home">
      <SEO
        path="/anthem"
        title="Foster Care Anthem Project | A Music House for Foster Care Advocacy"
        description="The Foster Care Anthem Project is an availability-based music initiative serving foster care recruitment, reform, and family support. 15 cornerstone tracks, campaign-ready editions, and the trafficking-awareness single 'We're Coming For You.'"
      />
      {/* HERO */}
      <section className="relative border-b border-an-border overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0 ken-burns" style={{backgroundImage:`url(${HERO_IMG})`, backgroundSize:"cover", backgroundPosition:"center"}} />
          <div className="absolute inset-0 bg-gradient-to-b from-an-bg/20 via-an-bg/60 to-an-bg" />
        </div>
        <div className="container-xl relative pt-24 pb-32 md:pt-32 md:pb-44 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 fade-up">
            <div className="overline text-an-gold mb-6">A Mission. A Catalog. A Reckoning.</div>
            <h1 className="font-display text-6xl md:text-8xl lg:text-[120px] leading-[0.9] text-an-ink">
              Fostercare <br/><span className="italic">anthemproject</span>
            </h1>
            <p className="mt-8 text-lg text-an-muted max-w-2xl">
              Music as a Vessel · Story as a Weapon · Redemption as the Goal.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/anthem/were-coming-for-you" className="btn-primary" data-testid="anthem-hero-wcfy">Hear the Anthem</Link>
              <Link to="/anthem/mission" className="btn-outline" data-testid="anthem-hero-mission">Read the Mission</Link>
            </div>
          </div>
        </div>
      </section>

      {/* CURRENT BATTLE — WCFY */}
      <section className="border-b border-an-border">
        <div className="container-xl py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-an-gold mb-3">Current Battle · April 30 Launch</div>
              <h2 className="font-display text-4xl md:text-6xl leading-tight">We're Coming <br/><span className="italic">For You.</span></h2>
            </div>
            <Link to="/anthem/were-coming-for-you" className="btn-outline" data-testid="anthem-wcfy-link">Open Anthem Page →</Link>
          </div>

          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <img src={WCFY_COVER} alt="We're Coming For You" className="w-full aspect-square object-cover" />
            </div>
            <div className="md:col-span-7 space-y-4 md:pl-8">
              <p className="text-an-muted leading-relaxed">
                A direct address to children trapped in human trafficking — and a vow to those fighting to bring them home. One single, three visual editions: R&B (Original), Hip-Hop, and Gospel.
              </p>
              <div className="grid grid-cols-3 gap-3 pt-4">
                {[
                  { label: "R&B · 01", sub: "Original", cover: WCFY_COVER },
                  { label: "Hip-Hop · 02", sub: "Street", cover: "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/gfc6slcn_FosterCareAnthem_Cover_4.png" },
                  { label: "Gospel · 03", sub: "Sanctuary", cover: "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/e7j7p9pk_FosterCareAnthem_Cover_5.png" },
                ].map((e) => (
                  <div key={e.label} className="border border-an-border p-2">
                    <img src={e.cover} alt={e.label} className="w-full aspect-square object-cover" />
                    <div className="pt-2 overline text-an-muted">{e.label}</div>
                    <div className="font-display text-base">{e.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ALBUMS */}
      <section className="border-b border-an-border">
        <div className="container-xl py-24">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <h2 className="font-display text-4xl md:text-6xl leading-tight">Two records. <span className="italic">One foundation.</span></h2>
            <Link to="/anthem/catalog" className="btn-outline" data-testid="anthem-catalog-link">Open Full Catalog</Link>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { cover: VOL1_COVER, title: "VOL I", sub: "15 Cornerstone Tracks · Jan 30, 2026", slug: "vol1" },
              { cover: EDITIONS_COVER, title: "VOL I · Anthem Editions", sub: "15 Campaign-Ready Versions", slug: "vol1-editions" },
            ].map((a) => (
              <Link key={a.slug} to="/anthem/catalog" className="group card-hover border border-an-border bg-an-surface p-5 block" data-testid={`anthem-album-${a.slug}`}>
                <img src={a.cover} alt={a.title} className="w-full aspect-square object-cover" />
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <div className="overline text-an-gold mb-1">Live · Streaming</div>
                    <div className="font-display text-2xl">{a.title}</div>
                    <div className="text-sm text-an-muted">{a.sub}</div>
                  </div>
                  <ArrowUpRight className="opacity-40 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION PULL */}
      <section>
        <div className="container-xl py-24 md:py-32 max-w-4xl">
          <div className="overline text-an-gold mb-5">The Mission</div>
          <blockquote className="font-display text-3xl md:text-5xl italic leading-tight">
            "This music exists to serve the mission — not the marketplace."
          </blockquote>
          <p className="mt-8 text-an-muted leading-relaxed">
            An availability-based initiative created in proximity to frontline foster care work — designed to amplify lived experience, humanize system-level conversations, and support organizations engaged in recruitment, reform, and family support.
          </p>
          <Link to="/anthem/mission" className="mt-10 inline-flex btn-outline" data-testid="anthem-mission-read">Read the Full Mission</Link>
        </div>
      </section>
    </div>
  );
}
